import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentSession, logoutUser } from "../api/auth";

export const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  loading: true,
  signIn: () => {},
  signOut: () => {},
  updateUser: () => {},
});

function readStoredUser() {
  // Purely a fast-paint cache so the UI doesn't flash "logged out" for a
  // moment while the real session check (below) is in flight. It is never
  // treated as the source of truth for whether the person is actually
  // logged in — only the server (via the httpOnly cookie) knows that.
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(readStoredUser());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const updateUser = useCallback((partial) => {
    setUser((prev) => {
      const next = { ...prev, ...partial };
      localStorage.setItem("user", JSON.stringify(next));
      return next;
    });
  }, []);

  // Ask the server "am I still logged in?" — this is the real check. The
  // cookie is httpOnly, so this is the only way to know; there's nothing
  // to inspect client-side.
  const checkSession = useCallback(async () => {
    try {
      const res = await getCurrentSession();
      if (res.data?.success) {
        setUser(res.data.user);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        setIsAuthenticated(true);
      } else {
        setUser(null);
        localStorage.removeItem("user");
        setIsAuthenticated(false);
      }
    } catch (error) {
      setUser(null);
      localStorage.removeItem("user");
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Called right after a successful login/register response. The server
  // has already set the auth cookie in that same response — this just
  // updates local UI state to match.
  const signIn = useCallback((nextUser) => {
    setUser(nextUser);
    localStorage.setItem("user", JSON.stringify(nextUser));
    setIsAuthenticated(true);
    setLoading(false);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await logoutUser();
    } catch (error) {
      // Clear local state regardless — worst case the cookie lingers
      // until it expires, but the app itself correctly treats the user
      // as logged out.
    }
    localStorage.removeItem("user");
    setUser(null);
    setIsAuthenticated(false);
    navigate("/login");
  }, [navigate]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      isAdmin: user?.roles === "admin",
      loading,
      signIn,
      signOut,
      updateUser,
    }),
    [user, isAuthenticated, loading, signIn, signOut, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
